import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PerformanceRules, PerformanceSettings } from './types';
import { performanceError, usePerformanceSettings } from './queries';

export function PerformanceSettingsForm({ report }: { report: PerformanceRules }) {
  const { t } = useTranslation();
  const { register, control, watch, handleSubmit } = useForm<PerformanceSettings>({ defaultValues: report.settings });
  const goal = watch('goal');
  const save = usePerformanceSettings(report.agencyId, report.workflowId);
  return (
    <Stack
      spacing={2}
      component="form"
      onSubmit={handleSubmit((values) =>
        save.mutate({
          ...values,
          successStepIds: values.goal === 'steps' ? values.successStepIds : [],
          transferWorkflowIds: values.goal === 'transfer' ? values.transferWorkflowIds : [],
        }),)}
    >
      <Typography variant="h6">{t('workflowPerformance.rules')}</Typography>
      <Alert severity="info">{t('workflowPerformance.rulesHint')}</Alert>
      {!report.settingsEditable && <Alert severity="info">{t('workflowPerformance.rulesReadOnly')}</Alert>}
      {report.options.truncated && <Alert severity="warning">{t('workflowPerformance.optionsLimited')}</Alert>}
      <TextField
        select
        value={goal}
        label={t('workflowPerformance.goal')}
        {...register('goal')}
        disabled={!report.settingsEditable || save.isLoading}
      >
        {(['none', 'steps', 'transfer'] as const).map((value) => (
          <MenuItem key={value} value={value}>
            {t(`workflowPerformance.goal_${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {goal !== 'none' && (
        <Controller
          control={control}
          name={goal === 'steps' ? 'successStepIds' : 'transferWorkflowIds'}
          rules={{
            validate: (value) => (value.length > 0 && value.length <= 20) || t('workflowPerformance.targetsRequired'),
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              select
              SelectProps={{ multiple: true }}
              label={t('workflowPerformance.targets')}
              disabled={!report.settingsEditable || save.isLoading}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            >
              {(goal === 'steps' ? report.options.steps : report.options.workflows).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}
      <Controller
        control={control}
        name="responseThresholdSeconds"
        rules={{
          validate: (value) =>
            (Number.isInteger(value) && value >= 60 && value <= 86400) || t('workflowPerformance.thresholdInvalid'),
        }}
        render={({ field, fieldState }) => (
          <TextField
            type="number"
            name={field.name}
            inputRef={field.ref}
            onBlur={field.onBlur}
            value={field.value / 60}
            onChange={(event) => field.onChange(Math.round(Number(event.target.value) * 60))}
            label={t('workflowPerformance.thresholdMinutes')}
            inputProps={{ min: 1, max: 1440, step: 'any', style: { fontSize: 16 } }}
            disabled={!report.settingsEditable || save.isLoading}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? t('workflowPerformance.thresholdHint')}
          />
        )}
      />
      {save.isError && <Alert severity="error">{t(performanceError(save.error))}</Alert>}
      {save.isSuccess && <Alert severity="success">{t('workflowPerformance.saved')}</Alert>}
      <Button
        type="submit"
        variant="contained"
        sx={{ alignSelf: 'flex-start' }}
        disabled={!report.settingsEditable || save.isLoading}
      >
        {t('workflowPerformance.save')}
      </Button>
    </Stack>
  );
}
